import React, { useCallback, useEffect, useMemo, useState } from "react";

const FILTERS = [
  ["beklemede", "Bekleyenler"],
  ["onaylandi", "Onaylananlar"],
  ["reddedildi", "Reddedilenler"],
  ["tumu", "Tümü"],
];

const COLORS = {
  navy: "#102a43",
  blue: "#1463b8",
  orange: "#f28c28",
  muted: "#627d98",
  border: "#d9e2ec",
  surface: "#f4f7fc",
};

async function readPayload(response) {
  const raw = await response.text();
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return { message: raw };
  }
}

function listFrom(payload) {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function descriptionValue(description, label) {
  const prefix = `${label}:`;
  return String(description || "")
    .split("\n")
    .find((line) => line.trim().startsWith(prefix))
    ?.trim()
    .slice(prefix.length)
    .trim() || "";
}

function statusStyle(status) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
  };
  if (status === "onaylandi") {
    return { ...base, color: "#166534", background: "#dcfce7" };
  }
  if (status === "reddedildi") {
    return { ...base, color: "#991b1b", background: "#fee2e2" };
  }
  return { ...base, color: "#92400e", background: "#fef3c7" };
}

function statusText(status) {
  if (status === "onaylandi") return "Onaylandı";
  if (status === "reddedildi") return "Reddedildi";
  return "İnceleme Bekliyor";
}

export default function HaritaBasvurulari({ API, token, setMessage }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("beklemede");
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [openById, setOpenById] = useState({});
  const [ownerById, setOwnerById] = useState({});
  const [search, setSearch] = useState("");

  const fetchApplications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/maps/admin/suggestions?status=${encodeURIComponent(filter)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const payload = await readPayload(response);
      if (!response.ok) throw new Error(payload?.message || "Harita başvuruları alınamadı");
      const list = listFrom(payload);
      setItems(list);
      setOwnerById((previous) => {
        const next = { ...previous };
        list.forEach((item) => {
          if (!next[item.id]) next[item.id] = String(item.kurum_old_id || 378);
        });
        return next;
      });
    } catch (error) {
      setItems([]);
      setMessage?.({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }, [API, filter, setMessage, token]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const visibleItems = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("tr-TR");
    if (!term) return items;
    return items.filter((item) =>
      [item.ad, item.adres, item.ulke_adi, item.sektor_adi, item.aciklama]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("tr-TR")
        .includes(term)
    );
  }, [items, search]);

  const decide = async (item, decision) => {
    const ownerId = Number(ownerById[item.id]);
    if (decision === "approve" && (!Number.isInteger(ownerId) || ownerId <= 0)) {
      setMessage?.({
        type: "error",
        text: "Yayın sahibi olarak geçerli bir kurum old_id değeri yazın.",
      });
      return;
    }
    const verb = decision === "approve" ? "onaylamak" : "reddetmek";
    if (!window.confirm(`“${item.ad}” başvurusunu ${verb} istiyor musunuz?`)) return;

    setActionId(item.id);
    try {
      const response = await fetch(
        `${API}/maps/admin/suggestions/${item.id}/${decision}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(
            decision === "approve" ? { kurum_old_id: ownerId } : {}
          ),
        }
      );
      const payload = await readPayload(response);
      if (!response.ok) throw new Error(payload?.message || "İşlem tamamlanamadı");
      setMessage?.({ type: "success", text: payload?.message || "İşlem tamamlandı" });
      await fetchApplications();
    } catch (error) {
      setMessage?.({ type: "error", text: error.message });
    } finally {
      setActionId(null);
    }
  };

  return (
    <section style={{ marginTop: 26 }}>
      <div style={{
        padding: 22,
        borderRadius: 24,
        background: "#fff",
        border: `1px solid ${COLORS.border}`,
        boxShadow: "0 18px 45px rgba(16,42,67,.08)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ color: COLORS.orange, fontSize: 11, fontWeight: 900, letterSpacing: ".09em" }}>
              TOPLULUK DOĞRULAMA MERKEZİ
            </div>
            <h2 style={{ margin: "6px 0 5px", color: COLORS.navy, fontSize: 25 }}>
              🗺️ Harita Başvuruları
            </h2>
            <p style={{ margin: 0, color: COLORS.muted, lineHeight: 1.5 }}>
              Kullanıcıların önerdiği kurum ve işletmeleri inceleyin, doğru kuruma bağlayın ve haritada yayınlayın.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchApplications}
            disabled={loading}
            style={{
              alignSelf: "flex-start",
              padding: "11px 15px",
              border: 0,
              borderRadius: 13,
              color: "#fff",
              background: COLORS.blue,
              fontWeight: 900,
              cursor: loading ? "wait" : "pointer",
            }}
          >
            {loading ? "Yükleniyor..." : "Listeyi Yenile"}
          </button>
        </div>

        <div style={{ display: "flex", gap: 9, marginTop: 20, flexWrap: "wrap" }}>
          {FILTERS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              style={{
                padding: "9px 14px",
                border: `1px solid ${filter === value ? COLORS.blue : COLORS.border}`,
                borderRadius: 999,
                color: filter === value ? "#fff" : COLORS.navy,
                background: filter === value ? COLORS.blue : "#fff",
                fontWeight: 850,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Kurum, şehir veya sektör ara..."
            style={{
              flex: "1 1 250px",
              minWidth: 210,
              padding: "10px 14px",
              borderRadius: 13,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.navy,
              outline: "none",
            }}
          />
        </div>

        <div style={{ display: "grid", gap: 12, marginTop: 18 }}>
          {!loading && !visibleItems.length && (
            <div style={{ padding: 28, textAlign: "center", borderRadius: 18, color: COLORS.muted, background: COLORS.surface }}>
              <div style={{ fontSize: 32 }}>✓</div>
              <b style={{ display: "block", marginTop: 7, color: COLORS.navy }}>Bu görünümde başvuru yok</b>
              <span>Yeni topluluk başvuruları geldiğinde burada görünecek.</span>
            </div>
          )}

          {visibleItems.map((item) => {
            const open = Boolean(openById[item.id]);
            const city = descriptionValue(item.aciklama, "Şehir");
            const district = descriptionValue(item.aciklama, "İlçe / bölge");
            const email = descriptionValue(item.aciklama, "İletişim e-postası");
            const type = descriptionValue(item.aciklama, "Başvuru türü") || "İşletme";
            const mapUrl = `https://www.google.com/maps/search/?api=1&query=${item.latitude},${item.longitude}`;
            return (
              <article key={item.id} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 18, overflow: "hidden" }}>
                <button
                  type="button"
                  onClick={() => setOpenById((prev) => ({ ...prev, [item.id]: !open }))}
                  style={{
                    width: "100%",
                    padding: 15,
                    border: 0,
                    background: open ? "#f8fbff" : "#fff",
                    display: "grid",
                    gridTemplateColumns: "minmax(0,1fr) auto auto",
                    gap: 12,
                    alignItems: "center",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <b style={{ display: "block", color: COLORS.navy, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.ad || "İsimsiz başvuru"}
                    </b>
                    <span style={{ display: "block", color: COLORS.muted, fontSize: 12, marginTop: 4 }}>
                      {type} · {item.sektor_adi || "Sektör belirtilmedi"} · {city || item.ulke_adi || "Konum belirtilmedi"}
                    </span>
                  </span>
                  <span style={statusStyle(item.durum)}>{statusText(item.durum)}</span>
                  <b style={{ color: COLORS.blue, fontSize: 20 }}>{open ? "−" : "+"}</b>
                </button>

                {open && (
                  <div style={{ padding: 16, borderTop: `1px solid ${COLORS.border}`, background: "#fff" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 10 }}>
                      {[
                        ["Adres", item.adres || "-", [city, district].filter(Boolean).join(" / ")],
                        ["İletişim", item.telefon || "-", email || "E-posta yok"],
                        ["Web sitesi", item.web_sitesi || "-", `Başvuru #${item.id}`],
                        ["Koordinat", `${item.latitude}, ${item.longitude}`, item.ulke_adi || "-"],
                      ].map(([label, value, sub]) => (
                        <div key={label} style={{ padding: 13, borderRadius: 14, background: COLORS.surface }}>
                          <span style={{ display: "block", color: COLORS.muted, fontSize: 10, fontWeight: 900, letterSpacing: ".06em" }}>{label.toUpperCase()}</span>
                          <b style={{ display: "block", color: COLORS.navy, marginTop: 5, overflowWrap: "anywhere" }}>{value}</b>
                          <span style={{ display: "block", color: COLORS.muted, fontSize: 11, marginTop: 4 }}>{sub}</span>
                        </div>
                      ))}
                    </div>

                    {item.aciklama && (
                      <pre style={{ margin: "12px 0 0", padding: 13, borderRadius: 14, whiteSpace: "pre-wrap", font: "inherit", fontSize: 12, lineHeight: 1.55, color: "#334e68", background: "#fff8ee", border: "1px solid #ffd8a8" }}>
                        {item.aciklama}
                      </pre>
                    )}

                    <div style={{ display: "flex", gap: 9, marginTop: 13, alignItems: "center", flexWrap: "wrap" }}>
                      <a href={mapUrl} target="_blank" rel="noreferrer" style={{ padding: "10px 13px", borderRadius: 12, color: COLORS.blue, background: "#eaf2fb", fontWeight: 900, textDecoration: "none" }}>
                        Haritada kontrol et
                      </a>

                      {item.durum !== "onaylandi" && item.durum !== "reddedildi" && (
                        <>
                          <input
                            inputMode="numeric"
                            value={ownerById[item.id] || ""}
                            onChange={(event) => setOwnerById((prev) => ({
                              ...prev,
                              [item.id]: event.target.value.replace(/\D/g, ""),
                            }))}
                            placeholder="Yayın sahibi kurum old_id"
                            title="Topluluk kayıtları için varsayılan 378; mevcut kuruma bağlanacaksa onun old_id değeri"
                            style={{ padding: "10px 12px", borderRadius: 12, border: `1px solid ${COLORS.border}`, minWidth: 205 }}
                          />
                          <button
                            type="button"
                            disabled={actionId === item.id}
                            onClick={() => decide(item, "approve")}
                            style={{ padding: "10px 14px", border: 0, borderRadius: 12, color: "#fff", background: "#168a4a", fontWeight: 900, cursor: "pointer" }}
                          >
                            Düzelt ve Onayla
                          </button>
                          <button
                            type="button"
                            disabled={actionId === item.id}
                            onClick={() => decide(item, "reject")}
                            style={{ padding: "10px 14px", border: "1px solid #fecaca", borderRadius: 12, color: "#b91c1c", background: "#fff1f2", fontWeight: 900, cursor: "pointer" }}
                          >
                            Reddet
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
